import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dealsApi, conversionApi } from '@/lib/api';

export function useDeals(params?: Parameters<typeof dealsApi.list>[0]) {
  return useQuery({
    queryKey: ['deals', params],
    queryFn: () => dealsApi.list(params),
  });
}

export function useDealBoard(pipelineId?: string, search?: string) {
  return useQuery({
    queryKey: ['deals', 'board', pipelineId, search || ''],
    queryFn: () => dealsApi.board(pipelineId, search),
    // keep the current board visible while a new search loads
    placeholderData: (prev) => prev,
  });
}

export function useDeal(id: string | undefined) {
  return useQuery({
    queryKey: ['deals', id],
    queryFn: () => dealsApi.getById(id!),
    enabled: !!id,
  });
}

export function useCreateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: dealsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useUpdateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof dealsApi.update>[1] }) =>
      dealsApi.update(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', id] });
    },
  });
}

export function useMoveDealStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stageId }: { id: string; stageId: string }) =>
      dealsApi.moveStage(id, stageId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

/**
 * Optimistically move a deal between stages in the cached board data.
 * Returns a rollback function in case the API call fails.
 */
export function useOptimisticBoardMove(pipelineId?: string, search?: string) {
  const qc = useQueryClient();

  return (dealId: string, newStageId: string) => {
    const queryKey = ['deals', 'board', pipelineId, search || ''];
    const previousBoard = qc.getQueryData(queryKey);

    qc.setQueryData(queryKey, (old: any) => {
      if (!old) return old;
      let movedDeal: any = null;
      const stages = old.stages.map((stage: any) => {
        const deal = stage.deals.find((d: any) => d.id === dealId);
        if (deal) {
          movedDeal = deal;
          return {
            ...stage,
            deals: stage.deals.filter((d: any) => d.id !== dealId),
            count: stage.count - 1,
            totalValue: stage.totalValue - (Number(deal.value) || 0),
          };
        }
        return stage;
      });
      if (!movedDeal) return old;
      return {
        ...old,
        stages: stages.map((stage: any) =>
          stage.id === newStageId
            ? { ...stage, deals: [...stage.deals, movedDeal], count: stage.count + 1, totalValue: stage.totalValue + (Number(movedDeal.value) || 0) }
            : stage
        ),
      };
    });

    return () => {
      if (previousBoard) qc.setQueryData(queryKey, previousBoard);
    };
  };
}

export function useMoveDealToPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pipelineId }: { id: string; pipelineId: string }) =>
      dealsApi.moveToPipeline(id, pipelineId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useCloseDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof dealsApi.close>[1] }) =>
      dealsApi.close(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', id] });
    },
  });
}

export function useReopenDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: dealsApi.reopen,
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', id] });
    },
  });
}

export function useDeleteDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: dealsApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useBulkDeleteDeals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dealIds: string[]) => dealsApi.bulkDelete(dealIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useBulkMovePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealIds, targetPipelineId }: { dealIds: string[]; targetPipelineId: string }) =>
      dealsApi.bulkMovePipeline(dealIds, targetPipelineId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useSavePaymentSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, data }: { dealId: string; data: Parameters<typeof dealsApi.savePaymentSchedule>[1] }) =>
      dealsApi.savePaymentSchedule(dealId, data),
    onSuccess: (_data, { dealId }) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', dealId] });
    },
  });
}

export function useDeletePaymentSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dealId: string) => dealsApi.deletePaymentSchedule(dealId),
    onSuccess: (_data, dealId) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', dealId] });
    },
  });
}

export function useConvertDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, data }: { dealId: string; data: Parameters<typeof conversionApi.convert>[1] }) =>
      conversionApi.convert(dealId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}
